import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, ListBullets, Warning, CheckCircle, CaretDown, ShieldCheck, Lightning,
} from "@phosphor-icons/react";
import { Helmet } from "react-helmet-async";
import { LCShieldLogo } from "@/components/LCShieldLogo";
import { Seo } from "@/components/Seo";
import { FooterLinks } from "@/components/FooterLinks";
import { getGuide, GUIDES, SOFTWARE_APP_SCHEMA } from "@/pages/resources/resourcesData";

const SITE = "https://getlcshield.com";

/* ---------- block renderers ---------- */
const P = ({ children }) => <p className="text-[15px] leading-relaxed text-lc-fog">{children}</p>;

const Bullet = ({ b, text }) => (
  <li className="flex gap-2.5 text-[15px] leading-relaxed text-lc-fog">
    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lc-sky" />
    <span>{b && <strong className="text-white">{b} </strong>}{text}</span>
  </li>
);

const Numbered = ({ n, text }) => (
  <li className="flex gap-3 text-[15px] leading-relaxed text-lc-fog">
    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-lc-sky/15 font-mono text-xs font-bold text-lc-sky">{n}</span>
    <span>{text}</span>
  </li>
);

const Callout = ({ tone = "warn", title, items }) => (
  <div className={`rounded-sm border p-5 ${tone === "warn" ? "border-lc-warn/30 bg-lc-warn/5" : "border-lc-ok/30 bg-lc-ok/5"}`}>
    {title && (
      <p className={`flex items-center gap-2 text-sm font-bold ${tone === "warn" ? "text-lc-warn" : "text-lc-ok"}`}>
        {tone === "warn" ? <Warning size={16} weight="fill" /> : <CheckCircle size={16} weight="fill" />}{title}
      </p>
    )}
    <ul className="mt-3 space-y-2">{items.map((it, i) => <Bullet key={i} {...it} />)}</ul>
  </div>
);

const Table = ({ headers, rows }) => (
  <div className="overflow-x-auto rounded-sm border border-white/10">
    <table className="w-full text-left text-sm">
      <thead>
        <tr className="border-b border-white/10 bg-lc-slate/40">
          {headers.map((h, i) => (
            <th key={i} className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-lc-sky">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-white/5 last:border-0">
            {r.map((c, j) => (
              <td key={j} className={`px-4 py-2.5 align-top ${j === 0 ? "font-mono text-xs text-white" : "text-lc-fog"}`}>{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Block = ({ block }) => {
  switch (block.type) {
    case "p": return <P>{block.text}</P>;
    case "ul": return <ul className="space-y-2.5">{block.items.map((it, i) => <Bullet key={i} {...it} />)}</ul>;
    case "ol": return <ol className="space-y-3">{block.items.map((it, i) => <Numbered key={i} n={i + 1} {...it} />)}</ol>;
    case "callout": return <Callout {...block} />;
    case "table": return <Table {...block} />;
    default: return null;
  }
};

/* ---------- FAQ accordion ---------- */
const FaqItem = ({ item, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-sm border border-white/10 bg-lc-slate/20" data-testid={`faq-item-${idx}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`faq-toggle-${idx}`}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left">
        <span className="text-[15px] font-semibold text-white">{item.q}</span>
        <CaretDown size={18} weight="bold" className={`shrink-0 text-lc-sky transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5"><P>{item.a}</P></div>}
    </div>
  );
};

export default function ResourceArticle() {
  const { slug } = useParams();
  const nav = useNavigate();
  const guide = getGuide(slug);
  const [activeId, setActiveId] = useState(guide?.toc?.[0]?.id);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    if (!guide) return;
    const onScroll = () => {
      let current = guide.toc[0]?.id;
      for (const t of guide.toc) {
        const el = document.getElementById(t.id);
        if (el && el.getBoundingClientRect().top <= 140) current = t.id;
      }
      setActiveId(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [guide]);

  if (!guide) {
    return (
      <div className="min-h-screen bg-lc-navy text-white flex flex-col items-center justify-center gap-4 px-6" data-testid="resource-not-found">
        <Seo title="Guide not found | LC Shield" robots="noindex, follow" />
        <p className="font-serif text-3xl">Guide not found</p>
        <Link to="/resources" className="inline-flex items-center gap-2 rounded-sm bg-lc-sky px-4 py-2 text-sm font-semibold text-lc-navy">
          <ArrowLeft weight="bold" size={15} /> Back to Resource Center
        </Link>
      </div>
    );
  }

  const url = `${SITE}/resources/${guide.slug}`;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: guide.title,
    description: guide.description,
    articleSection: guide.category,
    author: { "@type": "Organization", name: "LC Shield", url: SITE },
    publisher: { "@type": "Organization", name: "DZent Limited", url: SITE },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    url,
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: guide.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const goSection = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const related = GUIDES.filter((g) => g.slug !== guide.slug);

  return (
    <div className="min-h-screen bg-lc-navy text-white font-sans antialiased flex flex-col" data-testid="resource-article">
      <Seo path={`/resources/${guide.slug}`} title={`${guide.title} | LC Shield`} description={guide.description} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(SOFTWARE_APP_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* NAV */}
      <nav className="sticky top-0 z-40 border-b border-white/10 bg-lc-navy/85 backdrop-blur-md">
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="article-logo">
            <LCShieldLogo className="text-lc-sky" size={24} />
            <span className="font-serif text-xl tracking-tight">LC Shield</span>
          </Link>
          <button onClick={() => nav("/workspace")} data-testid="article-open-workspace"
            className="inline-flex items-center gap-2 rounded-sm bg-lc-sky px-4 py-2 text-sm font-semibold text-lc-navy hover:bg-white transition-colors duration-200">
            Open Workspace <ArrowRight weight="bold" size={15} />
          </button>
        </div>
      </nav>

      {/* HEADER */}
      <header className="border-b border-white/10">
        <div className="mx-auto max-w-6xl px-6 py-14">
          <Link to="/resources" className="inline-flex items-center gap-1.5 text-xs text-lc-fog hover:text-white transition-colors duration-200" data-testid="article-back-index">
            <ArrowLeft size={13} weight="bold" /> Resource Center
          </Link>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="mt-6 flex flex-wrap items-center gap-3 font-mono text-[11px] uppercase tracking-widest">
              <span className="rounded-sm border border-lc-sky/30 bg-lc-sky/10 px-2.5 py-1 text-lc-sky">{guide.category}</span>
              <span className="text-lc-fog">{guide.readTime}</span>
              <span className="text-lc-fog">{guide.updated}</span>
            </div>
            <h1 className="mt-5 max-w-3xl font-serif font-semibold tracking-tight text-4xl sm:text-5xl leading-[1.08]">{guide.title}</h1>
            <p className="mt-6 max-w-2xl text-lg text-lc-fog leading-relaxed">{guide.intro}</p>
          </motion.div>
        </div>
      </header>

      {/* BODY: sticky TOC + article */}
      <main className="flex-1">
        <div className="mx-auto max-w-6xl px-6 py-14 grid lg:grid-cols-12 gap-10">
          {/* TOC */}
          <aside className="lg:col-span-3">
            <div className="sticky top-24" data-testid="article-toc">
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-lc-sky">
                <ListBullets size={15} weight="bold" /> On this page
              </p>
              <nav className="mt-4 space-y-1 border-l border-white/10">
                {guide.toc.map((t) => (
                  <button key={t.id} onClick={() => goSection(t.id)} data-testid={`toc-${t.id}`}
                    className={`block w-full border-l-2 -ml-px pl-4 py-1.5 text-left text-[13px] leading-snug transition-colors duration-200 ${
                      activeId === t.id ? "border-lc-sky text-white" : "border-transparent text-lc-fog hover:text-white"
                    }`}>
                    {t.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* ARTICLE */}
          <article className="lg:col-span-9 space-y-14">
            {guide.sections.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-24 space-y-4">
                <h2 className="font-serif text-2xl sm:text-3xl text-white">{s.h}</h2>
                {s.blocks.map((b, i) => <Block key={i} block={b} />)}
              </section>
            ))}

            {/* FAQ */}
            <section className="space-y-4" data-testid="article-faq">
              <h2 className="font-serif text-2xl sm:text-3xl text-white">Frequently asked questions</h2>
              <div className="space-y-3">
                {guide.faq.map((f, i) => <FaqItem key={i} item={f} idx={i} />)}
              </div>
            </section>

            {/* CTA BANNER */}
            <div className="rounded-sm border border-lc-sky/30 bg-gradient-to-br from-lc-slate/60 to-lc-navy p-8" data-testid="article-cta-banner">
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-lc-sky">
                <ShieldCheck size={16} weight="duotone" /> Try it yourself
              </div>
              <h3 className="mt-3 font-serif text-2xl sm:text-3xl text-white">Try a free audit</h3>
              <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-lc-fog">
                Paste your SWIFT MT700 or trade documents and let LC Shield flag UCP 600 / ISBP 821 (2023) discrepancies — with article references and suggested fixes — in seconds. No credit card; documents are not saved to our servers after analysis.
              </p>
              <button onClick={() => nav("/workspace")} data-testid="article-cta-button"
                className="group mt-6 inline-flex items-center gap-2 rounded-sm bg-lc-sky px-6 py-3.5 text-sm font-semibold text-lc-navy hover:bg-white transition-colors duration-200">
                <Lightning weight="fill" size={16} /> Try a free audit
                <ArrowRight weight="bold" size={15} className="group-hover:translate-x-0.5 transition-transform duration-200" />
              </button>
            </div>

            {/* RELATED */}
            <section className="border-t border-white/10 pt-10" data-testid="article-related">
              <p className="font-mono text-[11px] uppercase tracking-widest text-lc-sky">Related guides</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {related.map((g) => (
                  <Link key={g.slug} to={`/resources/${g.slug}`} data-testid={`related-${g.slug}`}
                    className="group rounded-sm border border-white/10 bg-lc-slate/20 p-5 transition-colors duration-200 hover:border-lc-sky/50">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-lc-sky">{g.category}</span>
                    <p className="mt-2 font-serif text-lg leading-snug text-white group-hover:text-lc-sky transition-colors duration-200">{g.title}</p>
                  </Link>
                ))}
              </div>
            </section>
          </article>
        </div>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-white/10">
        <div className="mx-auto max-w-6xl px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <LCShieldLogo className="text-lc-sky" size={20} />
            <span className="font-serif text-lg text-white">LC Shield</span>
          </div>
          <FooterLinks variant="dark" />
        </div>
      </footer>
    </div>
  );
}
